import { useState, useEffect, useMemo } from 'react';
import countries from './data/countries';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import Breadcrumb from './Breadcrumb';
import { Container, Row, Col, Card, Image, Form, Button, Badge, Table, Pagination, Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import icLoginShape from './assets/img/ic-user-circle.svg';
import { exportReportingUsersPdf } from './api/exportReportingUsersPdf.js';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import defaultProfileImage from './assets/img/ic-img.svg';
import { resolveMediaUrl } from './utils/mediaUrl';

const userClassRows = [
    { label: 'Talent', key: 'talent' },
    { label: 'Company', key: 'company' },
    { label: 'Partner', key: 'partner' },
    { label: 'Abroadify', key: 'abroadify' },
];

const userTypeRows = [
    { label: 'User', key: 'user' },
    { label: 'Admin', key: 'admin' },
    { label: 'Super Admin', key: 'superAdmin' },
];

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString() : '—');

const formatDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatShortDate = (isoDate) => {
    if (!isoDate) return '—';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ROLE_TO_USER_CLASS = {
    student: 'Talent',
    company: 'Company',
    admin: 'Abroadify',
    superadmin: 'Abroadify',
    titanAdmin: 'Abroadify',
    donatorUser: 'Partner',
};

const ROLE_TO_USER_TYPE = {
    student: 'Student',
    company: 'Company',
    admin: 'Admin',
    superadmin: 'Super Admin',
    titanAdmin: 'Titan Admin',
    donatorUser: 'Donator',
};

const periodOptions = [
    { value: 'today', label: 'Today' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'allTime', label: 'All Time' },
];

const getPeriodRange = (period) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const today = now.toISOString().split('T')[0];
    const quarterStart = Math.floor(m / 3) * 3;
    switch (period) {
        case 'today':
            return { from: today, to: today };
        case 'thisMonth':
            return {
                from: new Date(y, m, 1).toISOString().split('T')[0],
                to: new Date(y, m + 1, 0).toISOString().split('T')[0],
            };
        case 'thisQuarter':
            return {
                from: new Date(y, quarterStart, 1).toISOString().split('T')[0],
                to: new Date(y, quarterStart + 3, 0).toISOString().split('T')[0],
            };
        case 'thisYear':
            return {
                from: new Date(y, 0, 1).toISOString().split('T')[0],
                to: new Date(y, 11, 31).toISOString().split('T')[0],
            };
        case 'allTime':
            return { from: '', to: '' };
        default:
            return null;
    }
};

const DEFAULT_PERIOD = 'today';

const ReportingUsersList = () => {
    const [tickets, setTickets] = useState([]);
    const [usersStats, setUsersStats] = useState(null);
    const [latestUsers, setLatestUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(10);

    const initRange = getPeriodRange(DEFAULT_PERIOD);
    const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD);
    const [fromDate, setFromDate] = useState(initRange?.from || '');
    const [toDate, setToDate] = useState(initRange?.to || '');
    const [selectedActivityType, setSelectedActivityType] = useState('');
    const [selectedUserFilter, setSelectedUserFilter] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCity, setSelectedCity] = useState('');

    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [filterSetName, setFilterSetName] = useState('');
    const [showDeleteFilterModal, setShowDeleteFilterModal] = useState(false);
    const [filterSetToDelete, setFilterSetToDelete] = useState(null);

    useEffect(() => {
        const range = getPeriodRange(selectedPeriod);
        if (range) {
            setFromDate(range.from);
            setToDate(range.to);
        }
    }, [selectedPeriod]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedActivityType, selectedUserFilter, selectedCompany, selectedCountry, selectedCity, fromDate, toDate]);

    const uniqueActivityTypes = useMemo(() =>
        [...new Set(latestUsers.map((e) => e.activityType).filter(Boolean))].sort(),
        [latestUsers]
    );

    const uniqueUserNames = useMemo(() => {
        const names = latestUsers.map((e) => {
            const u = e.user || {};
            return (u.firstName || u.familyName)
                ? `${u.firstName || ''} ${u.familyName || ''}`.trim()
                : u.email || null;
        }).filter(Boolean);
        return [...new Set(names)].sort();
    }, [latestUsers]);

    const uniqueCompanies = useMemo(() =>
        [...new Set(latestUsers.map((e) => e.user?.company?.title).filter(Boolean))].sort(),
        [latestUsers]
    );

    const countryNameByCode = useMemo(() =>
        new Map((countries || []).map((c) => [
            String(c.id || '').trim().toUpperCase(),
            c.name || String(c.id || '').trim().toUpperCase(),
        ])),
        []
    );

    const uniqueCountries = useMemo(() =>
        [...new Set(latestUsers.map((e) => e.user?.country).filter(Boolean))]
            .map((code) => ({
                value: code,
                label: countryNameByCode.get(String(code).trim().toUpperCase()) || code,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        [latestUsers, countryNameByCode]
    );

    const uniqueCities = useMemo(() =>
        [...new Set(
            latestUsers
                .filter((e) => !selectedCountry || e.user?.country === selectedCountry)
                .map((e) => e.user?.city)
                .filter(Boolean)
        )].sort(),
        [latestUsers, selectedCountry]
    );

    const filteredUsers = useMemo(() => {
        return latestUsers.filter((entry) => {
            const u = entry.user || {};
            const userName = (u.firstName || u.familyName)
                ? `${u.firstName || ''} ${u.familyName || ''}`.trim()
                : u.email || '';
            if (selectedActivityType && entry.activityType !== selectedActivityType) return false;
            if (selectedUserFilter && userName !== selectedUserFilter) return false;
            if (selectedCompany && u.company?.title !== selectedCompany) return false;
            if (selectedCountry && u.country !== selectedCountry) return false;
            if (selectedCity && u.city !== selectedCity) return false;
            return true;
        });
    }, [latestUsers, selectedActivityType, selectedUserFilter, selectedCompany, selectedCountry, selectedCity]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    const currentUsers = filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

    useEffect(() => {
        const fetchFilterSets = async () => {
            try {
                const res = await api.get('/api/reporting-filter-sets?page=reporting-users');
                setSavedFilterSets(Array.isArray(res.data) ? res.data : []);
            } catch (error) {
                console.error('Error fetching reporting users filter sets:', error);
            }
        };
        fetchFilterSets();
    }, []);

    const buildCurrentFiltersPayload = () => ({
        selectedPeriod,
        selectedActivityType,
        selectedUserFilter,
        selectedCompany,
        selectedCountry,
        selectedCity,
    });

    const applySavedFilterSet = (filters = {}) => {
        if (filters.selectedPeriod) setSelectedPeriod(filters.selectedPeriod);
        setSelectedActivityType(filters.selectedActivityType || '');
        setSelectedUserFilter(filters.selectedUserFilter || '');
        setSelectedCompany(filters.selectedCompany || '');
        setSelectedCountry(filters.selectedCountry || '');
        setSelectedCity(filters.selectedCity || '');
    };

    const handleSaveFilters = async (name) => {
        try {
            const trimmedName = String(name || '').trim();
            if (!trimmedName) return;

            const res = await api.post('/api/reporting-filter-sets', {
                page: 'reporting-users',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            });
            const saved = res.data;

            setSavedFilterSets((prev) => {
                const without = prev.filter((s) => s._id !== saved._id && s.name !== saved.name);
                return [saved, ...without];
            });
            setSelectedFilterSet(saved._id);
        } catch (error) {
            console.error('Error saving filter set:', error);
            alert(error?.response?.data?.msg || 'An error occurred while saving the filter set.');
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);
        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }
        const match = savedFilterSets.find((s) => s._id === value);
        if (match?.filters) applySavedFilterSet(match.filters);
    };

    const handleDeleteFilterSet = async (filterSetId) => {
        if (!filterSetId || filterSetId === 'default') return;
        try {
            await api.delete(`/api/reporting-filter-sets/${filterSetId}`);
            const wasSelected = String(selectedFilterSet) === String(filterSetId);
            setSavedFilterSets((prev) => prev.filter((s) => String(s._id) !== String(filterSetId)));
            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet({});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const clearFilters = () => {
        setSelectedActivityType('');
        setSelectedUserFilter('');
        setSelectedCompany('');
        setSelectedCountry('');
        setSelectedCity('');
        setCurrentPage(1);
    };

    const returnToDefault = () => {
        setSelectedPeriod(DEFAULT_PERIOD);
        clearFilters();
    };

    const handleExportPdf = async () => {
        try {
            await exportReportingUsersPdf({ usersStats, fromDate, toDate });
        } catch (error) {
            console.error('Error exporting reporting PDF:', error);
            alert('An error occurred while exporting the reporting PDF.');
        }
    };

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const res = await api.get('/api/tickets');
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        const fetchUsersStats = async () => {
            try {
                const params = {};
                if (fromDate) params.fromDate = fromDate;
                if (toDate) params.toDate = toDate;
                const res = await api.get('/api/reporting/users', { params });
                setUsersStats(res.data);
            } catch (error) {
                console.error('Error fetching users report:', error);
            }
        };
        fetchUsersStats();
    }, [fromDate, toDate]);

    useEffect(() => {
        const fetchLatestActivities = async () => {
            try {
                const params = {};
                if (fromDate) params.fromDate = fromDate;
                if (toDate) params.toDate = toDate;
                const res = await api.get('/api/reporting/users/latest-activities', { params });
                setLatestUsers(res.data);
            } catch (error) {
                console.error('Error fetching latest activities:', error);
            }
        };
        fetchLatestActivities();
    }, [fromDate, toDate]);

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body>
                                    <div className="filter discovery-filter-bar">
                                        <div className="d-block">
                                            <Row className="mb-3 align-items-center">
                                                <Col xs={12} md>
                                                    <h1 className="mb-0">Users</h1>
                                                </Col>
                                                <Col xs="auto">
                                                    <div style={{ minWidth: '160px', width: 'auto' }}>
                                                        {(() => {
                                                            const filterSetOptions = [
                                                                { value: 'default', label: 'Default filter set', isDefault: true },
                                                                ...savedFilterSets.map((fs) => ({
                                                                    _id: fs._id,
                                                                    value: fs._id,
                                                                    label: fs.name,
                                                                    name: fs.name,
                                                                    isDefault: false,
                                                                })),
                                                            ];
                                                            const selectedOption = filterSetOptions.find((o) => o.value === selectedFilterSet) || filterSetOptions[0];

                                                            const FilterSetOption = (props) => (
                                                                <components.Option {...props}>
                                                                    <div className="d-flex align-items-center justify-content-between w-100">
                                                                        <span>{props.data.label}</span>
                                                                        {!props.data.isDefault && (
                                                                            <button
                                                                                type="button"
                                                                                className="filter-set-delete-btn"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    setFilterSetToDelete(props.data);
                                                                                    setShowDeleteFilterModal(true);
                                                                                }}
                                                                                aria-label={`Delete ${props.data.label} filter set`}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </components.Option>
                                                            );

                                                            return (
                                                                <Select
                                                                    options={filterSetOptions}
                                                                    value={selectedOption}
                                                                    onChange={(opt) => handleFilterSetChange(opt?.value || 'default')}
                                                                    isSearchable={false}
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    className="react-select sm"
                                                                    classNamePrefix="react-select"
                                                                    components={{ Option: FilterSetOption }}
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="g-3 align-items-end flex-nowrap">
                                                <Col md={6} lg>
                                                    <Form.Group>
                                                        <Form.Label>Period</Form.Label>
                                                        <Form.Select
                                                            value={selectedPeriod}
                                                            onChange={(e) => setSelectedPeriod(e.target.value)}
                                                        >
                                                            {periodOptions.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6} lg>
                                                    <Form.Group>
                                                        <Form.Label>Activity type</Form.Label>
                                                        <Form.Select
                                                            value={selectedActivityType}
                                                            onChange={(e) => setSelectedActivityType(e.target.value)}
                                                        >
                                                            <option value="">All activity types</option>
                                                            {uniqueActivityTypes.map((type) => (
                                                                <option key={type} value={type}>{type}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6} lg>
                                                    <Form.Group>
                                                        <Form.Label>User</Form.Label>
                                                        <Form.Select
                                                            value={selectedUserFilter}
                                                            onChange={(e) => setSelectedUserFilter(e.target.value)}
                                                        >
                                                            <option value="">All users</option>
                                                            {uniqueUserNames.map((name) => (
                                                                <option key={name} value={name}>{name}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6} lg>
                                                    <Form.Group>
                                                        <Form.Label>Company</Form.Label>
                                                        <Form.Select
                                                            value={selectedCompany}
                                                            onChange={(e) => setSelectedCompany(e.target.value)}
                                                        >
                                                            <option value="">All companies</option>
                                                            {uniqueCompanies.map((company) => (
                                                                <option key={company} value={company}>{company}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6} lg>
                                                    <Form.Group>
                                                        <Form.Label>Country</Form.Label>
                                                        <Form.Select
                                                            value={selectedCountry}
                                                            onChange={(e) => {
                                                                setSelectedCountry(e.target.value);
                                                                setSelectedCity('');
                                                            }}
                                                        >
                                                            <option value="">All countries</option>
                                                            {uniqueCountries.map((country) => (
                                                                <option key={country.value} value={country.value}>{country.label}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6} lg>
                                                    <Form.Group>
                                                        <Form.Label>City</Form.Label>
                                                        <Form.Select
                                                            value={selectedCity}
                                                            onChange={(e) => setSelectedCity(e.target.value)}
                                                        >
                                                            <option value="">All cities</option>
                                                            {uniqueCities.map((city) => (
                                                                <option key={city} value={city}>{city}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col xs="auto" className="d-flex align-items-end">
                                                    <Button
                                                        variant="primary"
                                                        className="btn-sm discovery-filter-save-btn"
                                                        onClick={() => setShowSaveFilterModal(true)}
                                                    >
                                                        Save filters
                                                    </Button>
                                                </Col>
                                            </Row>

                                        </div>

                                        <Row className="filter-results">
                                            <Col className="d-flex align-items-center flex-wrap gap-2">
                                                <span>Showing results:</span>

                                                <Badge bg="info">
                                                    {periodOptions.find((o) => o.value === selectedPeriod)?.label}{' '}
                                                    <Button variant="link" size="sm" onClick={() => setSelectedPeriod(DEFAULT_PERIOD)}>×</Button>
                                                </Badge>

                                                {selectedActivityType && (
                                                    <Badge bg="info">
                                                        Activity: {selectedActivityType}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setSelectedActivityType('')}>×</Button>
                                                    </Badge>
                                                )}

                                                {selectedUserFilter && (
                                                    <Badge bg="info">
                                                        User: {selectedUserFilter}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setSelectedUserFilter('')}>×</Button>
                                                    </Badge>
                                                )}

                                                {selectedCompany && (
                                                    <Badge bg="info">
                                                        Company: {selectedCompany}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setSelectedCompany('')}>×</Button>
                                                    </Badge>
                                                )}

                                                {selectedCountry && (
                                                    <Badge bg="info">
                                                        Country: {countryNameByCode.get(String(selectedCountry).trim().toUpperCase()) || selectedCountry}{' '}
                                                        <Button variant="link" size="sm" onClick={() => { setSelectedCountry(''); setSelectedCity(''); }}>×</Button>
                                                    </Badge>
                                                )}

                                                {selectedCity && (
                                                    <Badge bg="info">
                                                        City: {selectedCity}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setSelectedCity('')}>×</Button>
                                                    </Badge>
                                                )}

                                                <Button variant="link" size="sm" className="ms-auto" onClick={clearFilters}>
                                                    Clear all
                                                </Button>
                                                <Button variant="link" size="sm" onClick={returnToDefault}>
                                                    Return to default
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="section-box">
                                        <Row>
                                            <Col md={4}>
                                                <div className="stat-box">
                                                    <div className="stat-box-icon">
                                                        <Image src={icLoginShape} alt="Total Logins" />
                                                    </div>
                                                    <span className="stat-box-label mb-3">Total Logins</span>
                                                    <span className="stat-box-value">{formatCount(usersStats?.totalLogins)}</span>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">User Class</div>
                                                    {userClassRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(usersStats?.userClass?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">User Types</div>
                                                    {userTypeRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(usersStats?.userTypes?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div className="section-box mt-4">
                                        <Row className="mb-4">
                                            <Col>
                                                <h3 className="mb-0">Latest Activities</h3>
                                            </Col>
                                            <Col xs="auto">
                                                <Button variant="outline-secondary" className="btn-sm" onClick={handleExportPdf}>
                                                    Export results
                                                </Button>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th><div>Date</div></th>
                                                                <th className="th-fixed-company"><div>Name</div></th>
                                                                <th><div>User class</div></th>
                                                                <th><div>User types</div></th>
                                                                <th><div>Company</div></th>
                                                                <th><div>Activity type</div></th>
                                                                <th><div>Country</div></th>
                                                                <th><div>Comment</div></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentUsers.map((entry) => {
                                                                const u = entry.user || {};
                                                                return (
                                                                    <tr key={entry._id}>
                                                                        <td>{formatShortDate(entry.createdAt)}</td>
                                                                        <td>
                                                                            <div>
                                                                                <img
                                                                                    src={resolveMediaUrl(u.profileImage) || defaultProfileImage}
                                                                                    alt={u.firstName}
                                                                                    className="round"
                                                                                />
                                                                                <strong>
                                                                                    {(u.firstName || u.familyName)
                                                                                        ? `${u.firstName || ''} ${u.familyName || ''}`.trim()
                                                                                        : (u.email || 'No name')}
                                                                                </strong>
                                                                            </div>
                                                                        </td>
                                                                        <td>{ROLE_TO_USER_CLASS[u.role] || u.role}</td>
                                                                        <td>{ROLE_TO_USER_TYPE[u.role] || u.role}</td>
                                                                        <td>{u.company?.title || 'N/A'}</td>
                                                                        <td>{entry.activityType}</td>
                                                                        <td>{u.country || '—'}</td>
                                                                        <td>
                                                                            {entry.commentRef?.comment
                                                                                ? <span title={entry.commentRef.comment}>{entry.commentRef.comment.length > 60 ? `${entry.commentRef.comment.slice(0, 60)}…` : entry.commentRef.comment}</span>
                                                                                : '—'}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row className="pagination-wrapper">
                                            <Col className="d-flex align-items-center">
                                                <span>
                                                    Total {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
                                                </span>
                                                <Pagination>
                                                    <Pagination.Prev
                                                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        <BsChevronLeft />
                                                    </Pagination.Prev>
                                                    {[...Array(totalPages).keys()].map((n) => (
                                                        <Pagination.Item
                                                            key={n + 1}
                                                            active={n + 1 === currentPage}
                                                            onClick={() => setCurrentPage(n + 1)}
                                                        >
                                                            {n + 1}
                                                        </Pagination.Item>
                                                    ))}
                                                    <Pagination.Next
                                                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                                        disabled={currentPage === totalPages || totalPages === 0}
                                                    >
                                                        <BsChevronRight />
                                                    </Pagination.Next>
                                                </Pagination>
                                            </Col>
                                            <Col className="pagination-controls">
                                                <div className="pagination-controls-per-page">
                                                    <Form.Select
                                                        value={usersPerPage}
                                                        onChange={(e) => { setUsersPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                                    >
                                                        <option value="10">10 / page</option>
                                                        <option value="20">20 / page</option>
                                                        <option value="40">40 / page</option>
                                                    </Form.Select>
                                                </div>
                                                <label className="pagination-controls-goto-label" htmlFor="reporting-users-goto">
                                                    Go to
                                                </label>
                                                <Form.Control
                                                    id="reporting-users-goto"
                                                    className="pagination-controls-goto-input"
                                                    type="number"
                                                    value={currentPage}
                                                    onChange={(e) => {
                                                        const p = Number(e.target.value);
                                                        if (p >= 1 && p <= totalPages) setCurrentPage(p);
                                                    }}
                                                    onBlur={() => {
                                                        if (currentPage < 1) setCurrentPage(1);
                                                        if (currentPage > totalPages) setCurrentPage(totalPages);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal
                className="with-form"
                show={showSaveFilterModal}
                onHide={() => { setShowSaveFilterModal(false); setFilterSetName(''); }}
                centered
            >
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    const trimmed = filterSetName.trim();
                    if (!trimmed) return;
                    await handleSaveFilters(trimmed);
                    setShowSaveFilterModal(false);
                    setFilterSetName('');
                }}>
                    <Modal.Header closeButton>
                        <Modal.Title>Save filter set as</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>This will create a new set of filters on the Users reporting page.</p>
                        <Form.Group className="mb-0">
                            <Form.Label>Filter set name</Form.Label>
                            <Form.Control
                                type="text"
                                value={filterSetName}
                                onChange={(e) => setFilterSetName(e.target.value)}
                                placeholder="Enter filter set name"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowSaveFilterModal(false); setFilterSetName(''); }}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="ms-3" type="submit" disabled={!filterSetName.trim()}>
                            Save
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal
                className="with-form"
                show={showDeleteFilterModal}
                onHide={() => { setShowDeleteFilterModal(false); setFilterSetToDelete(null); }}
                centered
            >
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!filterSetToDelete?._id && !filterSetToDelete?.value) return;
                    await handleDeleteFilterSet(filterSetToDelete._id || filterSetToDelete.value);
                    setShowDeleteFilterModal(false);
                    setFilterSetToDelete(null);
                }}>
                    <Modal.Header closeButton>
                        <Modal.Title>Are you sure you want to delete "{filterSetToDelete?.name || filterSetToDelete?.label}"?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Deleting this filter set will remove all its saved criteria.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowDeleteFilterModal(false); setFilterSetToDelete(null); }}>
                            No, keep this filter set
                        </Button>
                        <Button variant="primary" className="ms-3" type="submit">
                            Yes, delete this filter set
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default ReportingUsersList;
